import type {
  BulkAccessScope,
  BulkAccessTarget,
  ClauseBulkAccessTarget,
  ClauseBulkApplyTo,
  ClauseBulkSelectionItem,
} from '../types/AccessControl';

export function countTargetsByType(targets: BulkAccessTarget[]): {
  documentCount: number;
  folderCount: number;
} {
  let folderCount = 0;
  let documentCount = 0;

  for (const target of targets) {
    if (target.type === 'folder') {
      folderCount += 1;
    } else {
      documentCount += 1;
    }
  }

  return { documentCount, folderCount };
}

export function countClauseBulkTargets(targets: ClauseBulkAccessTarget[]): {
  clauseCount: number;
  variationCount: number;
} {
  let clauseCount = 0;
  let variationCount = 0;

  for (const target of targets) {
    if (target.type === 'clause') {
      clauseCount += 1;
    } else {
      variationCount += 1;
    }
  }

  return { clauseCount, variationCount };
}

export function toClauseBulkAccessTargets(
  items: ClauseBulkSelectionItem[],
): ClauseBulkAccessTarget[] {
  return items.map((item) => ({
    id: item.key,
    name: item.name,
    type: item.type,
  }));
}

export function getBulkAccessScopeFromCounts({
  documentCount,
  folderCount,
}: {
  documentCount: number;
  folderCount: number;
}): BulkAccessScope {
  if (folderCount > 0 && documentCount > 0) {
    return 'both';
  }
  if (folderCount > 0) {
    return 'folders';
  }
  return 'documents';
}

export function getDefaultBulkAccessScope(
  targets: BulkAccessTarget[],
): BulkAccessScope {
  return getBulkAccessScopeFromCounts(countTargetsByType(targets));
}

export function filterTargetsByScope(
  targets: BulkAccessTarget[],
  scope: BulkAccessScope,
): BulkAccessTarget[] {
  if (scope === 'both') {
    return targets;
  }
  if (scope === 'folders') {
    return targets.filter((target) => target.type === 'folder');
  }
  return targets.filter((target) => target.type === 'document');
}

export function getDefaultClauseBulkApplyTo(
  targets: ClauseBulkAccessTarget[],
): ClauseBulkApplyTo {
  const { clauseCount, variationCount } = countClauseBulkTargets(targets);
  if (clauseCount > 0 && variationCount > 0) {
    return 'clause_and_variation';
  }
  if (variationCount > 0) {
    return 'variation';
  }
  return 'clause';
}

export function filterTargetsByApplyTo(
  targets: ClauseBulkAccessTarget[],
  applyTo: ClauseBulkApplyTo,
): ClauseBulkAccessTarget[] {
  if (applyTo === 'clause_and_variation') {
    return targets;
  }
  if (applyTo === 'clause') {
    return targets.filter((target) => target.type === 'clause');
  }
  return targets.filter((target) => target.type === 'clause_variant');
}
